<template lang="pug">
base-section#person-details
  base-page-content
    .person-details-shell
      person-details-card(:person='person')
      .person-tabs-shell(v-if='hasSupplementalContent')
        LazyPersonTabs(
          :person='person',
          :projects='projects',
          :bibtexString='bibtexString',
          :theses='theses'
          :hydrate-on-visible="{ rootMargin: '200px' }"
        )
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PersonalProjects } from '~/interfaces/project'
import type { PersonInterface } from '~/interfaces/person'
import type { Thesis } from '~/models/thesis'

defineOptions({
  name: 'PersonDetailsSection',
})

const props = defineProps({
  person: {
    type: Object as () => PersonInterface,
    required: true,
  },
  projects: {
    type: Object as () => PersonalProjects,
    required: true,
  },
  bibtexString: {
    type: String,
    default: '',
  },
  theses: {
    type: Array as () => Array<Thesis>,
    required: true,
  },
})

const hasSupplementalContent = computed(() => {
  const about = props.person.en.about
  const activities = props.person.en.activities
  const awards = props.person.en.awards

  const aboutPopulated =
    !!about &&
    ((about.researchFoci && about.researchFoci.length > 0) ||
      (about.education && about.education.length > 0) ||
      (about.positions && about.positions.length > 0) ||
      (about.appointments && about.appointments.length > 0))

  const projectsPopulated =
    props.projects.inProgress.length > 0 ||
    props.projects.definitelyFinished.length > 0

  const activitiesPopulated =
    !!activities &&
    (activities.reviewer.length > 0 || activities.programCommittee.length > 0)

  const awardsPopulated = !!awards && awards.length > 0
  const thesesPopulated = props.theses.length > 0

  return (
    aboutPopulated ||
    projectsPopulated ||
    activitiesPopulated ||
    awardsPopulated ||
    thesesPopulated
  )
})
</script>

<style scoped>
#person-details {
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface-muted) 92%, var(--surface-page)) 0%,
      var(--surface-page) 18rem
    ),
    radial-gradient(
      circle at top left,
      rgba(0, 192, 216, 0.08),
      transparent 30rem
    );
}

.person-tabs-shell {
  padding: 1.25rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.person-details-shell {
  display: grid;
  gap: 1.5rem;
}

@media (max-width: 760px) {
  .person-tabs-shell {
    padding: 1rem;
  }
}
</style>
